import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, GraduationCap, Loader2, Plus, Edit3, Trash2, Eye, EyeOff,
  BarChart3, TrendingUp, Users, Award, IndianRupee, BookOpen, CheckCircle2, X,
  Settings, ListChecks, Sparkles, Save, RefreshCw, ChevronRight, Download, Send, Zap, FileText,
  Image as ImageIcon,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function adminHeaders() {
  const tok = localStorage.getItem("admin_token") || sessionStorage.getItem("admin_token");
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

export default function AdminShansoLearnPage() {
  const [tab, setTab] = useState("dashboard"); // dashboard | courses | enrollments | live-certs

  return (
    <div className="text-white">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5 pb-4 border-b border-zinc-800/60">
        <div className="flex items-center gap-2">
          <GraduationCap size={20} className="text-cyan-300" />
          <p className="text-white text-xl font-bold">ShansoLearn</p>
        </div>
        <nav className="flex items-center gap-1 text-[13px]">
          {[
            ["dashboard", "Dashboard", BarChart3],
            ["courses", "Courses", BookOpen],
            ["enrollments", "Enrollments", Users],
            ["live-certs", "Live Cohort Certs", Award],
          ].map(([k, label, Icon]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 transition-colors ${tab === k ? "bg-cyan-500/10 text-cyan-300 border border-cyan-500/40" : "text-zinc-400 hover:text-white border border-transparent"}`}
              data-testid={`tab-${k}`}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </nav>
      </div>

      <div>
        {tab === "dashboard" && <DashboardTab />}
        {tab === "courses" && <CoursesTab />}
        {tab === "enrollments" && <EnrollmentsTab />}
        {tab === "live-certs" && <LiveCohortCertsTab />}
      </div>
    </div>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────────────
function DashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/shanso-learn/analytics`, { headers: adminHeaders() })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load analytics"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Center><Loader2 className="animate-spin" /> Loading…</Center>;
  if (err) return <Center>{err}</Center>;
  if (!data) return null;

  const k = data.kpis;
  const f = data.funnel;

  return (
    <div className="space-y-6" data-testid="dashboard-tab">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={Users} color="cyan" label="Enrollments" value={k.total_enrollments} />
        <Kpi icon={IndianRupee} color="emerald" label="Total revenue" value={`₹${(k.total_revenue_inr || 0).toLocaleString("en-IN")}`} />
        <Kpi icon={Award} color="amber" label="Certificates issued" value={k.certificates_issued} sub={`${k.completion_pct}% completion`} />
        <Kpi icon={TrendingUp} color="violet" label="Avg exam score" value={`${k.avg_exam_score_pct}%`} sub={`${k.exam_pass_count} passed`} />
      </div>

      {/* Funnel */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl p-5">
        <p className="text-zinc-300 text-sm font-bold mb-4 inline-flex items-center gap-1.5"><BarChart3 size={14} /> Conversion funnel</p>
        <FunnelBar steps={[
          ["Enrolled", f.enrolled],
          ["Started learning", f.started_learning],
          ["Finished all modules", f.finished_all_modules],
          ["Took final exam", f.took_final_exam],
          ["Earned certificate", f.earned_certificate],
        ]} />
      </div>

      {/* Per course */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl overflow-hidden">
        <p className="text-zinc-300 text-sm font-bold p-4 inline-flex items-center gap-1.5"><BookOpen size={14} /> Per-course performance</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="per-course-table">
            <thead className="bg-zinc-950/60 text-zinc-400 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Course</th>
                <th className="text-left px-4 py-2 font-semibold">Status</th>
                <th className="text-right px-4 py-2 font-semibold">Modules</th>
                <th className="text-right px-4 py-2 font-semibold">Enrolled</th>
                <th className="text-right px-4 py-2 font-semibold">Certs</th>
                <th className="text-right px-4 py-2 font-semibold">Completion</th>
                <th className="text-right px-4 py-2 font-semibold">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/40">
              {data.per_course.map((c) => (
                <tr key={c.course_id} className="hover:bg-zinc-900/60">
                  <td className="px-4 py-3"><Link to={`/admin/shanso-learn/course/${c.course_id}`} className="text-cyan-300 hover:underline">{c.title}</Link></td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${c.status === "published" ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300"}`}>{c.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right text-zinc-300">{c.modules_count}</td>
                  <td className="px-4 py-3 text-right text-zinc-300">{c.enrollments_count}</td>
                  <td className="px-4 py-3 text-right text-zinc-300">{c.certificates_issued}</td>
                  <td className="px-4 py-3 text-right text-cyan-300 font-semibold">{c.completion_pct}%</td>
                  <td className="px-4 py-3 text-right text-emerald-300 font-semibold">₹{(c.revenue_inr || 0).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent enrollments */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl overflow-hidden">
        <p className="text-zinc-300 text-sm font-bold p-4 inline-flex items-center gap-1.5"><Users size={14} /> Recent enrollments</p>
        {data.recent_enrollments.length === 0 ? (
          <p className="px-4 pb-4 text-zinc-500 text-sm">No enrollments yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-enrollments-table">
              <thead className="bg-zinc-950/60 text-zinc-400 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Course</th>
                  <th className="text-left px-4 py-2 font-semibold">Status</th>
                  <th className="text-right px-4 py-2 font-semibold">Price</th>
                  <th className="text-right px-4 py-2 font-semibold">Exam</th>
                  <th className="text-left px-4 py-2 font-semibold">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/40">
                {data.recent_enrollments.map((e, i) => (
                  <tr key={i} className="hover:bg-zinc-900/60">
                    <td className="px-4 py-3 text-zinc-300">{e.course_title}</td>
                    <td className="px-4 py-3">
                      {e.certificate_id ? (
                        <span className="text-emerald-300 text-[11px]"><CheckCircle2 size={11} className="inline" /> Certified</span>
                      ) : e.paid ? (
                        <span className="text-cyan-300 text-[11px]">Enrolled</span>
                      ) : (
                        <span className="text-zinc-500 text-[11px]">Pending</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-zinc-300">₹{(e.price_inr || 0).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-right text-zinc-300">{e.last_exam_score_pct != null ? `${e.last_exam_score_pct}%` : "—"}</td>
                    <td className="px-4 py-3 text-zinc-500 text-xs">{e.paid_at ? new Date(e.paid_at).toLocaleString() : e.created_at ? new Date(e.created_at).toLocaleString() : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── COURSES LIST ──────────────────────────────────────────────────────
function CoursesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const r = await axios.get(`${API}/admin/courses`, { headers: adminHeaders() });
    setItems(r.data.items || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function togglePublish(c) {
    if (c.status === "published") {
      // Just update status to draft via the update endpoint — but we don't have that, so use update
      await axios.put(`${API}/admin/courses/${c.course_id}`, { status: "draft" }, { headers: adminHeaders() }).catch(() => {});
    } else {
      try {
        await axios.post(`${API}/admin/courses/${c.course_id}/publish`, {}, { headers: adminHeaders() });
      } catch (e) {
        alert(e?.response?.data?.detail || "Publish failed");
        return;
      }
    }
    load();
  }
  async function deleteCourse(c) {
    if (!window.confirm(`Delete course "${c.title}"? This is irreversible.`)) return;
    await axios.delete(`${API}/admin/courses/${c.course_id}`, { headers: adminHeaders() });
    load();
  }

  if (loading) return <Center><Loader2 className="animate-spin" /> Loading…</Center>;

  return (
    <div className="space-y-4" data-testid="courses-tab">
      <SeedPromptEngineeringBanner items={items} onSeeded={load} />
      <SeedClaudeForEveryoneBanner items={items} onSeeded={load} />
      <SeedGenerativeAIMasteryBanner items={items} onSeeded={load} />
      <SeedAgenticAiExpertBanner items={items} onSeeded={load} />
      <SeedAiPoweredDataAnalystBanner items={items} onSeeded={load} />
      <SeedLangGraphMasteryBanner items={items} onSeeded={load} />
      <SeedPythonUsingAiBanner items={items} onSeeded={load} />
      <SeedAiEraDataScientistBanner items={items} onSeeded={load} />
      <SeedQuickBitesBanner items={items} onSeeded={load} />
      <ReleaseAllQuickBitesBanner items={items} onSeeded={load} />
      <HandbookBackfillBanner />
      <RecoverWelcomesBanner />
      <SyncFlagshipCoversBanner items={items} onSeeded={load} />
      <div className="flex items-center justify-between">
        <p className="text-zinc-300 text-sm">{items.length} courses</p>
        <Link to="/admin/shanso-learn/course/new" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 text-sm" data-testid="new-course-btn">
          <Plus size={14} /> New course
        </Link>
      </div>
      <div className="space-y-2">
        {items.map((c) => (
          <div key={c.course_id} className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 flex items-start justify-between gap-3" data-testid={`course-row-${c.slug}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <p className="text-white font-bold text-base">{c.title}</p>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${c.status === "published" ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300"}`}>{c.status}</span>
              </div>
              <p className="text-zinc-500 text-xs">/{c.slug} · ₹{c.price_inr} · {(c.modules || []).length} modules · {c.cert_role || "no role set"}</p>
              {c.summary && <p className="text-zinc-400 text-sm mt-1 line-clamp-2">{c.summary}</p>}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Link to={`/admin/shanso-learn/course/${c.course_id}`} className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-300" title="Edit" data-testid={`edit-${c.slug}`}><Edit3 size={14} /></Link>
              <button onClick={() => togglePublish(c)} className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-300" title={c.status === "published" ? "Unpublish" : "Publish"} data-testid={`toggle-${c.slug}`}>
                {c.status === "published" ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <button onClick={() => deleteCourse(c)} className="p-2 rounded-lg hover:bg-red-500/20 text-red-300" title="Delete" data-testid={`delete-${c.slug}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── ENROLLMENTS ───────────────────────────────────────────────────────
function EnrollmentsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/shanso-learn/enrollments?limit=100`, { headers: adminHeaders() })
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Center><Loader2 className="animate-spin" /> Loading…</Center>;

  return (
    <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl overflow-hidden" data-testid="enrollments-tab">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950/60 text-zinc-400 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Learner</th>
              <th className="text-left px-4 py-2 font-semibold">Email</th>
              <th className="text-left px-4 py-2 font-semibold">Course</th>
              <th className="text-right px-4 py-2 font-semibold">Progress</th>
              <th className="text-right px-4 py-2 font-semibold">Exam</th>
              <th className="text-left px-4 py-2 font-semibold">Status</th>
              <th className="text-left px-4 py-2 font-semibold">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/40">
            {items.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-zinc-500 text-sm">No enrollments yet.</td></tr>
            )}
            {items.map((e) => (
              <tr key={e.enrollment_id} className="hover:bg-zinc-900/60">
                <td className="px-4 py-3 text-zinc-200 font-medium">{e.full_name}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{e.email}<br /><span className="text-zinc-600">{e.phone}</span></td>
                <td className="px-4 py-3 text-zinc-300">{e.course_title}</td>
                <td className="px-4 py-3 text-right text-cyan-300 font-semibold">{e.modules_passed}/{e.modules_total} ({e.progress_pct}%)</td>
                <td className="px-4 py-3 text-right text-zinc-300">{e.last_exam_score_pct != null ? `${e.last_exam_score_pct}%` : "—"}</td>
                <td className="px-4 py-3">
                  {e.certificate_id ? (
                    <span className="text-emerald-300 text-[11px] inline-flex items-center gap-0.5"><Award size={11} /> Certified</span>
                  ) : e.paid ? (
                    <span className="text-cyan-300 text-[11px]">Paid</span>
                  ) : (
                    <span className="text-zinc-500 text-[11px]">Unpaid</span>
                  )}
                </td>
                <td className="px-4 py-3 text-zinc-500 text-xs">{e.created_at ? new Date(e.created_at).toLocaleDateString() : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Shared ───────────────────────────────────────────────────────────
function Kpi({ icon: Icon, color, label, value, sub }) {
  const colors = {
    cyan: "from-cyan-500/20 to-cyan-500/0 border-cyan-500/30 text-cyan-300",
    emerald: "from-emerald-500/20 to-emerald-500/0 border-emerald-500/30 text-emerald-300",
    amber: "from-amber-500/20 to-amber-500/0 border-amber-500/30 text-amber-300",
    violet: "from-violet-500/20 to-violet-500/0 border-violet-500/30 text-violet-300",
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} bg-zinc-900/40 border rounded-2xl p-4`} data-testid={`kpi-${label.replace(/ /g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon size={14} />
        <p className="text-zinc-500 text-[10px] uppercase tracking-wider font-bold">{label}</p>
      </div>
      <p className="text-white text-2xl font-bold leading-none">{value}</p>
      {sub && <p className="text-zinc-500 text-[11px] mt-1">{sub}</p>}
    </div>
  );
}

function FunnelBar({ steps }) {
  const top = steps[0]?.[1] || 1;
  return (
    <div className="space-y-1.5">
      {steps.map(([label, value], i) => {
        const w = Math.round((value / Math.max(1, top)) * 100);
        const drop = i > 0 ? Math.round(((steps[i - 1][1] - value) / Math.max(1, steps[i - 1][1])) * 100) : 0;
        return (
          <div key={label} className="flex items-center gap-3 text-sm">
            <span className="text-zinc-400 text-[12px] w-44 shrink-0">{label}</span>
            <div className="flex-1 bg-zinc-950/60 rounded h-7 relative overflow-hidden border border-zinc-800/60">
              <div className="h-full bg-gradient-to-r from-cyan-500 to-cyan-700 rounded transition-all" style={{ width: `${w}%` }} />
              <span className="absolute inset-0 flex items-center pl-3 text-white font-bold text-xs">{value}</span>
            </div>
            {i > 0 && drop > 0 && <span className="text-red-300 text-[11px] w-12 text-right">−{drop}%</span>}
          </div>
        );
      })}
    </div>
  );
}

function Center({ children }) {
  return <div className="text-zinc-400 text-sm py-12 text-center flex items-center justify-center gap-2">{children}</div>;
}


// ─── One-click seeder for "Generative AI Mastery" pre-built course bundle ──
function SeedGenerativeAIMasteryBanner({ items, onSeeded }) {
  const existing = (items || []).find((c) => c.slug === "generative-ai-mastery");
  const needsSeed = !existing || existing.status !== "published" || (existing.modules || []).length < 10;
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState("");

  if (!needsSeed) return null;

  async function seedNow() {
    setRunning(true); setMsg("Seeding 10 modules + 30 exam Qs from packaged bundle…");
    try {
      const r = await axios.post(
        `${API}/admin/courses/seed-from-bundle/generative_ai_mastery`,
        {},
        { headers: adminHeaders() }
      );
      if (!r.data?.ok) throw new Error(r.data?.detail || "Seed failed");
      setMsg(`✓ ${r.data.action} · ${r.data.modules_count} modules · ${r.data.exam_questions} exam Qs · status=${r.data.status}`);
      await onSeeded();
    } catch (e) {
      setMsg("✗ " + (e?.response?.data?.detail || e?.message || "Failed"));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div
      className="rounded-xl border border-cyan-500/40 bg-gradient-to-br from-cyan-900/25 via-zinc-900/40 to-violet-900/15 p-4 sm:p-5 flex flex-col gap-3"
      data-testid="seed-gam-banner"
    >
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/15 border border-cyan-500/40 flex items-center justify-center shrink-0">
            <Sparkles size={18} className="text-cyan-300" />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-bold leading-tight">
              "Generative AI Mastery" course is not live in this environment
            </p>
            <p className="text-zinc-400 text-xs mt-0.5">
              One click loads 10 pre-rendered video modules (~3.5 hours), 50 quizzes, and a 30-question final exam from the packaged JSON bundle.
            </p>
            {msg && (
              <p className={`text-xs mt-1.5 ${msg.startsWith("✓") ? "text-emerald-300" : msg.startsWith("✗") ? "text-red-300" : "text-cyan-300"}`} data-testid="seed-gam-status">
                {msg}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={seedNow}
          disabled={running}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-black font-bold text-sm disabled:opacity-60 whitespace-nowrap"
          style={{ background: "linear-gradient(90deg, #22D3EE, #8B5CF6)" }}
          data-testid="seed-gam-btn"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {running ? "Seeding…" : "Seed & publish Generative AI Mastery"}
        </button>
      </div>
    </div>
  );
}


// ─── One-click seeder for "Agentic AI Expert" pre-built course bundle ──
function SeedAgenticAiExpertBanner({ items, onSeeded }) {
  const existing = (items || []).find((c) => c.slug === "agentic-ai-expert");
  const needsSeed = !existing || existing.status !== "published" || (existing.modules || []).length < 14;
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState("");

  if (!needsSeed) return null;

  async function seedNow() {
    setRunning(true); setMsg("Seeding 14 modules + 30 exam Qs from packaged bundle…");
    try {
      const r = await axios.post(
        `${API}/admin/courses/seed-from-bundle/agentic_ai_expert`,
        {},
        { headers: adminHeaders() }
      );
      if (!r.data?.ok) throw new Error(r.data?.detail || "Seed failed");
      setMsg(`✓ ${r.data.action} · ${r.data.modules_count} modules · ${r.data.exam_questions} exam Qs · status=${r.data.status}`);
      await onSeeded();
    } catch (e) {
      setMsg("✗ " + (e?.response?.data?.detail || e?.message || "Failed"));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div
      className="rounded-xl border border-violet-500/40 bg-gradient-to-br from-violet-900/25 via-zinc-900/40 to-cyan-900/15 p-4 sm:p-5 flex flex-col gap-3"
      data-testid="seed-aae-banner"
    >
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-violet-500/15 border border-violet-500/40 flex items-center justify-center shrink-0">
            <Sparkles size={18} className="text-violet-300" />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-bold leading-tight">
              "Agentic AI Expert" course is not live in this environment
            </p>
            <p className="text-zinc-400 text-xs mt-0.5">
              One click loads 14 pre-rendered video modules, 70 quizzes, a 30-question final exam, and 5 downloadable agent-building PDF templates from the packaged JSON bundle.
            </p>
            {msg && (
              <p className={`text-xs mt-1.5 ${msg.startsWith("✓") ? "text-emerald-300" : msg.startsWith("✗") ? "text-red-300" : "text-violet-300"}`} data-testid="seed-aae-status">
                {msg}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={seedNow}
          disabled={running}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-black font-bold text-sm disabled:opacity-60 whitespace-nowrap"
          style={{ background: "linear-gradient(90deg, #8B5CF6, #22D3EE)" }}
          data-testid="seed-aae-btn"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {running ? "Seeding…" : "Seed & publish Agentic AI Expert"}
        </button>
      </div>
    </div>
  );
}


// ─── One-click seeder for "AI Powered Data Analyst" pre-built course bundle ──
function SeedAiPoweredDataAnalystBanner({ items, onSeeded }) {
  const existing = (items || []).find((c) => c.slug === "ai-powered-data-analyst");
  const needsSeed = !existing || existing.status !== "published" || (existing.modules || []).length < 10;
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState("");

  if (!needsSeed) return null;

  async function seedNow() {
    setRunning(true); setMsg("Seeding 10 modules + 30 exam Qs from packaged bundle…");
    try {
      const r = await axios.post(
        `${API}/admin/courses/seed-from-bundle/ai_powered_data_analyst`,
        {},
        { headers: adminHeaders() }
      );
      if (!r.data?.ok) throw new Error(r.data?.detail || "Seed failed");
      setMsg(`✓ ${r.data.action} · ${r.data.modules_count} modules · ${r.data.exam_questions} exam Qs · status=${r.data.status}`);
      await onSeeded();
    } catch (e) {
      setMsg("✗ " + (e?.response?.data?.detail || e?.message || "Failed"));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div
      className="rounded-xl border border-fuchsia-500/40 bg-gradient-to-br from-fuchsia-900/25 via-zinc-900/40 to-teal-900/15 p-4 sm:p-5 flex flex-col gap-3"
      data-testid="seed-apda-banner"
    >
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-fuchsia-500/15 border border-fuchsia-500/40 flex items-center justify-center shrink-0">
            <Sparkles size={18} className="text-fuchsia-300" />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-bold leading-tight">
              "AI Powered Data Analyst" course is not live in this environment
            </p>
            <p className="text-zinc-400 text-xs mt-0.5">
              One click loads 10 pre-rendered video modules, 50 quizzes, a 30-question final exam, and 5 industry capstones with synthetic datasets + AI-graded review from the packaged JSON bundle.
            </p>
            {msg && (
              <p className={`text-xs mt-1.5 ${msg.startsWith("✓") ? "text-emerald-300" : msg.startsWith("✗") ? "text-red-300" : "text-fuchsia-300"}`} data-testid="seed-apda-status">
                {msg}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={seedNow}
          disabled={running}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-black font-bold text-sm disabled:opacity-60 whitespace-nowrap"
          style={{ background: "linear-gradient(90deg, #E879F9, #14B8A6)" }}
          data-testid="seed-apda-btn"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {running ? "Seeding…" : "Seed & publish AI Powered Data Analyst"}
        </button>
      </div>
    </div>
  );
}


// ─── One-click seeder for "LangGraph Mastery" pre-built course bundle ──
function SeedLangGraphMasteryBanner({ items, onSeeded }) {
  const existing = (items || []).find((c) => c.slug === "langgraph-mastery");
  const needsSeed = !existing || existing.status !== "published" || (existing.modules || []).length < 10;
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState("");

  if (!needsSeed) return null;

  async function seedNow() {
    setRunning(true); setMsg("Seeding 10 modules + 30 exam Qs from packaged bundle…");
    try {
      const r = await axios.post(
        `${API}/admin/courses/seed-from-bundle/langgraph_mastery`,
        {},
        { headers: adminHeaders() }
      );
      if (!r.data?.ok) throw new Error(r.data?.detail || "Seed failed");
      setMsg(`✓ ${r.data.action} · ${r.data.modules_count} modules · ${r.data.exam_questions} exam Qs · status=${r.data.status}`);
      await onSeeded();
    } catch (e) {
      setMsg("✗ " + (e?.response?.data?.detail || e?.message || "Failed"));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div
      className="rounded-xl border border-blue-500/40 bg-gradient-to-br from-blue-900/25 via-zinc-900/40 to-purple-900/15 p-4 sm:p-5 flex flex-col gap-3"
      data-testid="seed-lgm-banner"
    >
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-blue-500/15 border border-blue-500/40 flex items-center justify-center shrink-0">
            <Sparkles size={18} className="text-blue-300" />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-bold leading-tight">
              "LangGraph Mastery" course is not live in this environment
            </p>
            <p className="text-zinc-400 text-xs mt-0.5">
              One click loads 10 pre-rendered video modules (~3h 9min), 50 quizzes, a 30-question final exam, and 5 builder capstones (Customer Support, Deep Research, Code Reviewer, SDR, FP&A) with starter Python boilerplates + AI-graded review.
            </p>
            {msg && (
              <p className={`text-xs mt-1.5 ${msg.startsWith("✓") ? "text-emerald-300" : msg.startsWith("✗") ? "text-red-300" : "text-blue-300"}`} data-testid="seed-lgm-status">
                {msg}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={seedNow}
          disabled={running}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-white font-bold text-sm disabled:opacity-60 whitespace-nowrap"
          style={{ background: "linear-gradient(90deg, #3b82f6, #8b5cf6)" }}
          data-testid="seed-lgm-btn"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {running ? "Seeding…" : "Seed & publish LangGraph Mastery"}
        </button>
      </div>
    </div>
  );
}


// ─── One-click seeder for "Python Using AI" pre-built course bundle ──
function SeedPythonUsingAiBanner({ items, onSeeded }) {
  const existing = (items || []).find((c) => c.slug === "python-using-ai");
  const needsSeed = !existing || existing.status !== "published" || (existing.modules || []).length < 12;
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState("");

  if (!needsSeed) return null;

  async function seedNow() {
    setRunning(true); setMsg("Seeding 12 modules + 30 exam Qs from packaged bundle…");
    try {
      const r = await axios.post(
        `${API}/admin/courses/seed-from-bundle/python_using_ai`,
        {},
        { headers: adminHeaders() }
      );
      if (!r.data?.ok) throw new Error(r.data?.detail || "Seed failed");
      setMsg(`✓ ${r.data.action} · ${r.data.modules_count} modules · ${r.data.exam_questions} exam Qs · status=${r.data.status}`);
      await onSeeded();
    } catch (e) {
      setMsg("✗ " + (e?.response?.data?.detail || e?.message || "Failed"));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div
      className="rounded-xl border border-emerald-500/40 bg-gradient-to-br from-emerald-900/25 via-zinc-900/40 to-cyan-900/15 p-4 sm:p-5 flex flex-col gap-3"
      data-testid="seed-pua-banner"
    >
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center shrink-0">
            <Sparkles size={18} className="text-emerald-300" />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-bold leading-tight">
              "Python Using AI" course is not live in this environment
            </p>
            <p className="text-zinc-400 text-xs mt-0.5">
              One click loads 12 pre-rendered modules (~3h 33min), 60 quizzes, a 30-question final exam, and 6 builder capstones (Resume Analyzer, Data Analyst Assistant, Excel Auto-Reporter, Research Assistant, Productivity Agent, Chatbot+FastAPI) with starter Python boilerplates + AI-graded review.
            </p>
            {msg && (
              <p className={`text-xs mt-1.5 ${msg.startsWith("✓") ? "text-emerald-300" : msg.startsWith("✗") ? "text-red-300" : "text-emerald-300"}`} data-testid="seed-pua-status">
                {msg}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={seedNow}
          disabled={running}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-white font-bold text-sm disabled:opacity-60 whitespace-nowrap"
          style={{ background: "linear-gradient(90deg, #10b981, #06b6d4)" }}
          data-testid="seed-pua-btn"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {running ? "Seeding…" : "Seed & publish Python Using AI"}
        </button>
      </div>
    </div>
  );
}


// ─── One-click seeder for "The AI-Era Data Scientist" pre-built course bundle ──
function SeedAiEraDataScientistBanner({ items, onSeeded }) {
  const existing = (items || []).find((c) => c.slug === "ai-era-data-scientist");
  const needsSeed = !existing || existing.status !== "published" || (existing.modules || []).length < 10;
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState("");

  if (!needsSeed) return null;

  async function seedNow() {
    setRunning(true); setMsg("Seeding 10 modules + 30 exam Qs from packaged bundle…");
    try {
      const r = await axios.post(
        `${API}/admin/courses/seed-from-bundle/ai_era_data_scientist`,
        {},
        { headers: adminHeaders() }
      );
      if (!r.data?.ok) throw new Error(r.data?.detail || "Seed failed");
      setMsg(`✓ ${r.data.action} · ${r.data.modules_count} modules · ${r.data.exam_questions} exam Qs · status=${r.data.status}`);
      await onSeeded();
    } catch (e) {
      setMsg("✗ " + (e?.response?.data?.detail || e?.message || "Failed"));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div
      className="rounded-xl border border-indigo-500/40 bg-gradient-to-br from-indigo-900/25 via-zinc-900/40 to-amber-900/15 p-4 sm:p-5 flex flex-col gap-3"
      data-testid="seed-aeds-banner"
    >
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-indigo-500/15 border border-indigo-500/40 flex items-center justify-center shrink-0">
            <Sparkles size={18} className="text-indigo-300" />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-bold leading-tight">
              "The AI-Era Data Scientist" course is not live in this environment
            </p>
            <p className="text-zinc-400 text-xs mt-0.5">
              One click loads 10 modules (~3h), 50 quizzes, a 30-question final exam, and 6 builder capstones (EDA Agent, Text-to-Insight, Embedding-First Classifier, AI Data Analyst Agent, End-to-End DS Solution, Talk-to-Your-Data Service) with starter ZIPs + AI-graded review.
            </p>
            {msg && (
              <p className={`text-xs mt-1.5 ${msg.startsWith("✓") ? "text-indigo-300" : msg.startsWith("✗") ? "text-red-300" : "text-indigo-300"}`} data-testid="seed-aeds-status">
                {msg}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={seedNow}
          disabled={running}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-white font-bold text-sm disabled:opacity-60 whitespace-nowrap"
          style={{ background: "linear-gradient(90deg, #6366f1, #f59e0b)" }}
          data-testid="seed-aeds-btn"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {running ? "Seeding…" : "Seed & publish AI-Era Data Scientist"}
        </button>
      </div>
    </div>
  );
}


// ─── One-click seeder for "Claude for Everyone" pre-built course bundle ──
function SeedClaudeForEveryoneBanner({ items, onSeeded }) {
  const existing = (items || []).find((c) => c.slug === "claude-for-everyone");
  const needsSeed = !existing || existing.status !== "published" || (existing.modules || []).length < 12;
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState("");

  if (!needsSeed) return null;

  async function seedNow() {
    setRunning(true); setMsg("Seeding 12 modules + 30 exam Qs from packaged bundle…");
    try {
      const r = await axios.post(
        `${API}/admin/courses/seed-from-bundle/claude_for_everyone`,
        {},
        { headers: adminHeaders() }
      );
      if (!r.data?.ok) throw new Error(r.data?.detail || "Seed failed");
      setMsg(`✓ ${r.data.action} · ${r.data.modules_count} modules · ${r.data.exam_questions} exam Qs · status=${r.data.status}`);
      await onSeeded();
    } catch (e) {
      setMsg("✗ " + (e?.response?.data?.detail || e?.message || "Failed"));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div
      className="rounded-xl border border-fuchsia-500/40 bg-gradient-to-br from-fuchsia-900/25 via-zinc-900/40 to-cyan-900/15 p-4 sm:p-5 flex flex-col gap-3"
      data-testid="seed-cfe-banner"
    >
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-fuchsia-500/15 border border-fuchsia-500/40 flex items-center justify-center shrink-0">
            <Sparkles size={18} className="text-fuchsia-300" />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-bold leading-tight">
              "Claude for Everyone" course is not live in this environment
            </p>
            <p className="text-zinc-400 text-xs mt-0.5">
              One click loads 12 pre-rendered video modules, 60 quizzes and a 30-question final exam from the packaged JSON bundle.
            </p>
            {msg && (
              <p className={`text-xs mt-1.5 ${msg.startsWith("✓") ? "text-emerald-300" : msg.startsWith("✗") ? "text-red-300" : "text-fuchsia-300"}`} data-testid="seed-cfe-status">
                {msg}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={seedNow}
          disabled={running}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-black font-bold text-sm disabled:opacity-60 whitespace-nowrap"
          style={{ background: "linear-gradient(90deg, #D946EF, #9333EA)" }}
          data-testid="seed-cfe-btn"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {running ? "Seeding…" : "Seed & publish Claude for Everyone"}
        </button>
      </div>
    </div>
  );
}

// ─── One-click seeder for "Quick Bites" tier (8 courses, ⚡ Sprint theme) ──
function SeedQuickBitesBanner({ items, onSeeded }) {
  // QB slugs all start with "qb-"
  const qbItems = (items || []).filter((c) => c.tier === "quick_bite" || (c.slug || "").startsWith("qb-"));
  const needsSeed = qbItems.length < 8;
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState("");

  if (!needsSeed && qbItems.length === 0) return null;

  async function seedNow() {
    setRunning(true); setMsg("Seeding 8 Quick Bites (32 module outlines)…");
    try {
      const r = await axios.post(
        `${API}/admin/courses/seed-quick-bites`,
        {},
        { headers: adminHeaders() }
      );
      if (!r.data?.ok) throw new Error(r.data?.detail || "Seed failed");
      const created = r.data.courses.filter((c) => c.action === "created").length;
      const updated = r.data.courses.length - created;
      const examsSeeded = r.data.exams_seeded ?? 0;
      const examTotal = r.data.courses.reduce((s, c) => s + (c.exam_questions || 0), 0);
      setMsg(
        `✓ ${created} created · ${updated} updated · ${r.data.count} courses · ` +
        `Final Assessments: ${examsSeeded}/${r.data.count} (${examTotal} questions)`
      );
      await onSeeded();
    } catch (e) {
      setMsg("✗ " + (e?.response?.data?.detail || e?.message || "Failed"));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div
      className="rounded-xl border p-4 sm:p-5 flex flex-col gap-3"
      style={{
        borderColor: "rgba(163,230,53,0.40)",
        background: "linear-gradient(135deg, rgba(163,230,53,0.10), rgba(250,204,21,0.05), rgba(24,24,27,0.6))",
      }}
      data-testid="seed-quick-bites-banner"
    >
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border"
            style={{ background: "rgba(163,230,53,0.15)", borderColor: "rgba(163,230,53,0.40)" }}
          >
            <Zap size={18} fill="#a3e635" className="text-lime-300" />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-bold leading-tight">
              {needsSeed
                ? `Quick Bites tier — ${qbItems.length}/8 courses seeded`
                : "Quick Bites tier seeded · re-run to update outlines"}
            </p>
            <p className="text-zinc-400 text-xs mt-0.5">
              ⚡ 80-minute sprint courses · ₹399 each · Pick 5 for ₹1,999. Course bodies arrive as videos render — they show as &ldquo;Coming Soon&rdquo; cards until then.
            </p>
            {msg && (
              <p
                className={`text-xs mt-1.5 ${msg.startsWith("✓") ? "text-emerald-300" : msg.startsWith("✗") ? "text-red-300" : "text-lime-300"}`}
                data-testid="seed-quick-bites-status"
              >
                {msg}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={seedNow}
          disabled={running}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-black font-extrabold text-sm disabled:opacity-60 whitespace-nowrap"
          style={{ background: "linear-gradient(135deg, #a3e635, #facc15)" }}
          data-testid="seed-quick-bites-btn"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} fill="currentColor" />}
          {running ? "Seeding…" : needsSeed ? "Seed & publish 8 Quick Bites" : "Re-sync outlines"}
        </button>
      </div>
    </div>
  );
}


// ─── One-click "Mark all Quick Bites LIVE" — for prod rollout ──────────
function ReleaseAllQuickBitesBanner({ items, onSeeded }) {
  const qbItems = (items || []).filter((c) => c.tier === "quick_bite" || (c.slug || "").startsWith("qb-"));
  const liveCount = qbItems.filter((c) => c.release_status === "live").length;
  const comingSoonCount = qbItems.length - liveCount;
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState("");

  // Only show if QBs exist AND at least one is still coming_soon
  if (qbItems.length === 0 || comingSoonCount === 0) return null;

  async function releaseAllLive() {
    setRunning(true); setMsg("Flipping all Quick Bites to LIVE…");
    try {
      const r = await axios.post(
        `${API}/admin/quick-bites/release-all?release_status=live`,
        {},
        { headers: adminHeaders() }
      );
      if (!r.data?.ok) throw new Error(r.data?.detail || "Release-all failed");
      setMsg(`✓ ${r.data.modified} flipped to LIVE · ${r.data.matched} total Quick Bites`);
      await onSeeded();
    } catch (e) {
      setMsg("✗ " + (e?.response?.data?.detail || e?.message || "Failed"));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div
      className="rounded-xl border p-4 sm:p-5 flex flex-col gap-3"
      style={{
        borderColor: "rgba(250,204,21,0.40)",
        background: "linear-gradient(135deg, rgba(250,204,21,0.10), rgba(239,68,68,0.05), rgba(24,24,27,0.6))",
      }}
      data-testid="release-all-quick-bites-banner"
    >
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border"
            style={{ background: "rgba(250,204,21,0.15)", borderColor: "rgba(250,204,21,0.40)" }}
          >
            <Zap size={18} fill="#facc15" className="text-yellow-300" />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-bold leading-tight">
              {comingSoonCount} Quick Bite{comingSoonCount === 1 ? "" : "s"} stuck on Coming Soon
            </p>
            <p className="text-zinc-400 text-xs mt-0.5">
              {liveCount}/{qbItems.length} are live. Click to instantly publish all remaining Quick Bites — videos & quizzes are already in storage, this just flips the catalog visibility flag. Safe to re-run.
            </p>
            {msg && (
              <p
                className={`text-xs mt-1.5 ${msg.startsWith("✓") ? "text-emerald-300" : msg.startsWith("✗") ? "text-red-300" : "text-yellow-200"}`}
                data-testid="release-all-quick-bites-status"
              >
                {msg}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={releaseAllLive}
          disabled={running}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-black font-extrabold text-sm disabled:opacity-60 whitespace-nowrap"
          style={{ background: "linear-gradient(135deg, #facc15, #fb923c)" }}
          data-testid="release-all-quick-bites-btn"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} fill="currentColor" />}
          {running ? "Publishing…" : `Mark all ${comingSoonCount} as LIVE`}
        </button>
      </div>
    </div>
  );
}


/* ─── Sync course cover images (flagship + Quick Bites, idempotent) ─── */
const COVER_SLUGS = [
  // Flagship
  "prompt-engineering",
  "claude-for-everyone",
  "generative-ai-mastery",
  // Quick Bites
  "qb-chatgpt5-power-users",
  "qb-ai-presentations",
  "qb-mcp-quickstart",
  "qb-rag",
  "qb-responsible-ai",
  "qb-ai-foundations",
  "qb-agentic-ai",
  "qb-llm-basics",
  "qb-ai-for-leaders",
];

function RecoverWelcomesBanner() {
  const [running, setRunning] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [msg, setMsg] = useState("");

  async function run(dry) {
    setRunning(true); setMsg(dry ? "Scanning enrollments…" : "Resending welcome emails…");
    try {
      const r = await axios.post(
        `${API}/admin/welcomes/recover?dry_run=${dry}`,
        {},
        { headers: adminHeaders() }
      );
      if (!r.data?.ok) throw new Error(r.data?.detail || "Recover failed");
      setDryRun(r.data);
      if (dry) {
        setMsg(`Dry-run · ${r.data.would_send} paid enrollment${r.data.would_send === 1 ? "" : "s"} missed the welcome email · ${r.data.skipped.length} skipped`);
      } else {
        setMsg(`✓ Resent welcome to ${r.data.sent}/${r.data.would_send} learners · ${r.data.errors.length} errors`);
      }
    } catch (e) {
      setMsg("✗ " + (e?.response?.data?.detail || e?.message || "Failed"));
    } finally {
      setRunning(false);
    }
  }

  const canSend = dryRun && dryRun.would_send > 0;
  return (
    <div
      className="rounded-xl border p-4 sm:p-5 flex flex-col gap-3"
      style={{
        borderColor: "rgba(251,191,36,0.40)",
        background: "linear-gradient(135deg, rgba(251,191,36,0.08), rgba(245,158,11,0.04), rgba(24,24,27,0.6))",
      }}
      data-testid="recover-welcomes-banner"
    >
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border"
            style={{ background: "rgba(251,191,36,0.15)", borderColor: "rgba(251,191,36,0.40)" }}
          >
            <Send size={18} className="text-amber-300" />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-bold leading-tight">
              Recover missed welcome emails
            </p>
            <p className="text-zinc-400 text-xs mt-0.5">
              Sweeps for paid enrollments where the welcome email never went out (typically when a learner closes the browser before the Cashfree redirect finishes). Dry-run first to preview the count.
            </p>
            {msg && (
              <p
                className={`text-xs mt-1.5 ${msg.startsWith("✓") ? "text-emerald-300" : msg.startsWith("✗") ? "text-red-300" : "text-amber-300"}`}
                data-testid="recover-welcomes-status"
              >
                {msg}
              </p>
            )}
            {dryRun && dryRun.sample?.length > 0 && (
              <div className="mt-2 space-y-1 text-[11px] text-zinc-400 break-words">
                {dryRun.sample.map((s, i) => (
                  <div key={i} className="border-l-2 border-amber-500/40 pl-2">
                    <div><span className="text-zinc-300 font-semibold">{s.email}</span> · {s.title}</div>
                    {s.last_error && (
                      <div className="text-red-300/90 mt-0.5">↳ last error ({s.attempts}× attempts): <code className="text-red-200">{s.last_error}</code></div>
                    )}
                  </div>
                ))}
                {dryRun.errors?.length > 0 && (
                  <div className="border-l-2 border-red-500/60 pl-2 mt-2 text-red-300">
                    Failures from this send: {dryRun.errors.length}. Most recent: <code className="text-red-200">{dryRun.errors[dryRun.errors.length - 1]?.error}</code>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <button
            onClick={() => run(true)}
            disabled={running}
            className="shrink-0 inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-lg bg-white/5 border border-amber-500/40 text-amber-200 font-bold text-xs hover:bg-white/10 disabled:opacity-60"
            data-testid="recover-welcomes-dry-run-btn"
          >
            {running && !canSend ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
            Dry-run
          </button>
          <button
            onClick={() => {
              if (!canSend) return;
              if (window.confirm(`Resend welcome email to ${dryRun.would_send} learner${dryRun.would_send === 1 ? "" : "s"}? Idempotent — re-running won't double-email anyone.`)) {
                run(false);
              }
            }}
            disabled={running || !canSend}
            className="shrink-0 inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-lg text-black font-extrabold text-xs disabled:opacity-50 whitespace-nowrap"
            style={{ background: canSend ? "linear-gradient(135deg, #FBBF24, #F59E0B)" : "#3f3f46" }}
            data-testid="recover-welcomes-send-btn"
          >
            {running && canSend ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Send to {dryRun?.would_send ?? "—"} learners
          </button>
        </div>
      </div>
    </div>
  );
}


function HandbookBackfillBanner() {
  const [running, setRunning] = useState(false);
  const [dryRun, setDryRun] = useState(null); // null | response object
  const [msg, setMsg] = useState("");

  async function run(dry) {
    setRunning(true); setMsg(dry ? "Running dry-run…" : "Sending handbooks…");
    try {
      const r = await axios.post(
        `${API}/admin/handbooks/backfill?dry_run=${dry}`,
        {},
        { headers: adminHeaders() }
      );
      if (!r.data?.ok) throw new Error(r.data?.detail || "Backfill failed");
      setDryRun(r.data);
      if (dry) {
        setMsg(`Dry-run · would email ${r.data.would_send} learner${r.data.would_send === 1 ? "" : "s"} · ${r.data.already_sent_previously} already received · ${r.data.skipped.length} skipped`);
      } else {
        setMsg(`✓ Sent ${r.data.sent}/${r.data.would_send} handbooks · ${r.data.errors.length} errors`);
      }
    } catch (e) {
      setMsg("✗ " + (e?.response?.data?.detail || e?.message || "Failed"));
    } finally {
      setRunning(false);
    }
  }

  const canSend = dryRun && dryRun.would_send > 0;
  return (
    <div
      className="rounded-xl border p-4 sm:p-5 flex flex-col gap-3"
      style={{
        borderColor: "rgba(6,182,212,0.40)",
        background: "linear-gradient(135deg, rgba(6,182,212,0.10), rgba(8,145,178,0.05), rgba(24,24,27,0.6))",
      }}
      data-testid="handbook-backfill-banner"
    >
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border"
            style={{ background: "rgba(6,182,212,0.15)", borderColor: "rgba(6,182,212,0.40)" }}
          >
            <FileText size={18} className="text-cyan-300" />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-bold leading-tight">
              Course Handbook backfill
            </p>
            <p className="text-zinc-400 text-xs mt-0.5">
              One-time blast — email a freshly-generated <b>Course Handbook PDF</b> to every learner who already earned a certificate. New certificate emails include the handbook automatically. Click &ldquo;Dry-run&rdquo; first to preview the recipient count.
            </p>
            {msg && (
              <p
                className={`text-xs mt-1.5 ${msg.startsWith("✓") ? "text-emerald-300" : msg.startsWith("✗") ? "text-red-300" : "text-cyan-300"}`}
                data-testid="handbook-backfill-status"
              >
                {msg}
              </p>
            )}
            {dryRun && dryRun.sample?.length > 0 && (
              <div className="mt-2 text-[11px] text-zinc-500">
                Sample: {dryRun.sample.map((s) => s.email).join(" · ")}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <button
            onClick={() => run(true)}
            disabled={running}
            className="shrink-0 inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-lg bg-white/5 border border-cyan-500/40 text-cyan-200 font-bold text-xs hover:bg-white/10 disabled:opacity-60"
            data-testid="handbook-dry-run-btn"
          >
            {running && !canSend ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
            Dry-run
          </button>
          <button
            onClick={() => {
              if (!canSend) return;
              if (window.confirm(`Email Course Handbook to ${dryRun.would_send} learner${dryRun.would_send === 1 ? "" : "s"}? This is one-time and idempotent — re-running won't re-send to anyone.`)) {
                run(false);
              }
            }}
            disabled={running || !canSend}
            className="shrink-0 inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-lg text-black font-extrabold text-xs disabled:opacity-50 whitespace-nowrap"
            style={{ background: canSend ? "linear-gradient(135deg, #22D3EE, #0891B2)" : "#3f3f46" }}
            data-testid="handbook-send-btn"
          >
            {running && canSend ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Send to {dryRun?.would_send ?? "—"} learners
          </button>
        </div>
      </div>
    </div>
  );
}


function SyncFlagshipCoversBanner({ items, onSeeded }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  // Only show if at least one tracked course is missing its cover_image_url
  const tracked = items.filter((c) => COVER_SLUGS.includes(c.slug));
  const missing = tracked.filter((c) => !c.cover_image_url);
  if (tracked.length === 0 || missing.length === 0) return null;

  async function syncCovers() {
    setRunning(true);
    setResult(null);
    const url = `${API}/admin/shanso-learn/backfill-course-covers`;
    try {
      const r = await axios.post(url, {}, { headers: adminHeaders() });
      setResult(r.data);
      await onSeeded();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Failed";
      setResult({ ok: false, error: `${status || "?"}: ${detail}`, url });
    } finally {
      setRunning(false);
    }
  }

  return (
    <div
      className="rounded-xl border p-4 sm:p-5 flex flex-col gap-3"
      style={{
        borderColor: "rgba(34,211,238,0.40)",
        background: "linear-gradient(135deg, rgba(34,211,238,0.10), rgba(112,0,255,0.05), rgba(24,24,27,0.6))",
      }}
      data-testid="sync-flagship-covers-banner"
    >
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border"
            style={{ background: "rgba(34,211,238,0.15)", borderColor: "rgba(34,211,238,0.40)" }}
          >
            <ImageIcon size={18} className="text-cyan-300" />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-bold leading-tight">
              {missing.length} course{missing.length === 1 ? "" : "s"} missing cover image
            </p>
            <p className="text-zinc-400 text-xs mt-0.5">
              Push canonical cover URLs ({missing.map((c) => c.slug).join(", ")}) to this environment&apos;s DB.
              Image files are deployed under <code className="text-cyan-300">/shansolearn-preview/</code>. Idempotent — safe to re-run.
            </p>
            {result && (
              <div
                className={`text-xs mt-1.5 ${result.ok ? "text-emerald-300" : "text-red-300"}`}
                data-testid="sync-flagship-covers-status"
              >
                {result.ok ? (
                  <>
                    ✓ {result.results.filter((r) => r.status === "updated").length} updated ·
                    {" "}{result.results.filter((r) => r.status === "already_set").length} already set ·
                    {" "}{result.results.filter((r) => r.status === "not_found").length} not found
                  </>
                ) : (
                  <>
                    ✗ {result.error}
                    {result.url && <span className="block text-zinc-500 mt-0.5 break-all">{result.url}</span>}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <button
          onClick={syncCovers}
          disabled={running}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-black font-extrabold text-sm disabled:opacity-60 whitespace-nowrap"
          style={{ background: "linear-gradient(135deg, #22d3ee, #7000ff)" }}
          data-testid="sync-flagship-covers-btn"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
          {running ? "Syncing…" : "Sync course covers"}
        </button>
      </div>
    </div>
  );
}





// ─── One-click seeder for the flagship Prompt Engineering course ──────────
function SeedPromptEngineeringBanner({ items, onSeeded }) {
  const promptCourse = (items || []).find((c) => c.slug === "prompt-engineering");
  const needsSeed = !promptCourse;
  const needsPublish = promptCourse && promptCourse.status !== "published";
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState("");
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  // Pre-flight: only check storage health when we actually need to seed
  // (not when we just need to flip draft→published).
  useEffect(() => {
    if (!needsSeed) return;
    let cancelled = false;
    setHealthLoading(true);
    axios.get(`${API}/courses/seed-health/prompt-engineering`)
      .then((r) => { if (!cancelled) setHealth(r.data); })
      .catch(() => { if (!cancelled) setHealth({ ready: false, summary: {}, error: true }); })
      .finally(() => { if (!cancelled) setHealthLoading(false); });
    return () => { cancelled = true; };
  }, [needsSeed]);

  if (!needsSeed && !needsPublish) return null;

  async function seedAndPublish() {
    setRunning(true); setMsg("");
    try {
      if (needsSeed) {
        setMsg("Seeding course (this can take 10-15s)…");
        const r = await axios.post(
          `${API}/admin/courses/seed-prompt-engineering`,
          {},
          { headers: adminHeaders() },
        );
        if (!r.data?.ok) throw new Error(r.data?.detail || "Seed failed");
        setMsg(
          `✓ Seeded · ${r.data.modules_with_video}/10 videos · ${r.data.quizzes_imported} quizzes · ${r.data.exam_questions_imported} exam Qs. Publishing…`
        );
      } else {
        setMsg("Publishing…");
      }
      await axios.post(
        `${API}/admin/courses/${promptCourse?.course_id || "_"}/publish`,
        {},
        { headers: adminHeaders() },
      ).catch(async () => {
        const r = await axios.get(`${API}/admin/courses`, { headers: adminHeaders() });
        const c = (r.data.items || []).find((x) => x.slug === "prompt-engineering");
        if (c) {
          await axios.post(
            `${API}/admin/courses/${c.course_id}/publish`,
            {},
            { headers: adminHeaders() },
          );
        }
      });
      setMsg("✓ Live on /courses. Refreshing list…");
      await onSeeded();
    } catch (e) {
      setMsg("✗ " + (e?.response?.data?.detail || e?.message || "Operation failed"));
    } finally {
      setRunning(false);
    }
  }

  const seedDisabled = running || (needsSeed && (healthLoading || !health?.ready));

  return (
    <div
      className="rounded-xl border border-cyan-500/40 bg-gradient-to-br from-cyan-900/25 via-zinc-900/40 to-fuchsia-900/15 p-4 sm:p-5 flex flex-col gap-3"
      data-testid="seed-banner"
    >
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/15 border border-cyan-500/40 flex items-center justify-center shrink-0">
            <Sparkles size={18} className="text-cyan-300" />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-bold leading-tight">
              {needsSeed ? "Prompt Engineering course is not yet in this environment" : "Prompt Engineering exists but is in draft"}
            </p>
            <p className="text-zinc-400 text-xs mt-0.5">
              {needsSeed
                ? "One click hydrates 10 video modules, 50 quizzes and the 30-question final exam from shared storage."
                : "Click below to publish so learners can discover it on /courses."}
            </p>
            {msg && (
              <p className={`text-xs mt-1.5 ${msg.startsWith("✓") ? "text-emerald-300" : msg.startsWith("✗") ? "text-red-300" : "text-cyan-300"}`} data-testid="seed-status">
                {msg}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={seedAndPublish}
          disabled={seedDisabled}
          className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-black font-bold text-sm disabled:opacity-60 whitespace-nowrap"
          style={{ background: "linear-gradient(90deg, #22D3EE, #0891B2)" }}
          data-testid="seed-prompt-eng-btn"
        >
          {running
            ? <><Loader2 size={14} className="animate-spin" /> Working…</>
            : <><Sparkles size={14} /> {needsSeed ? "Seed & publish" : "Publish now"}</>}
        </button>
      </div>

      {needsSeed && (
        <StorageHealthStrip loading={healthLoading} health={health} />
      )}
    </div>
  );
}

function StorageHealthStrip({ loading, health }) {
  if (loading) {
    return (
      <div className="rounded-lg border border-zinc-800 bg-zinc-950/40 p-3 text-zinc-400 text-xs inline-flex items-center gap-2" data-testid="seed-health-loading">
        <Loader2 size={12} className="animate-spin" /> Checking storage…
      </div>
    );
  }
  if (!health) return null;
  const s = health.summary || {};
  const rows = [
    { label: "outline.json", ok: s.outline_present, detail: s.outline_present ? "loaded" : "missing" },
    { label: "Module plans", ok: (s.plans_present || 0) === 10, detail: `${s.plans_present || 0} / 10` },
    { label: "Module videos", ok: (s.videos_present || 0) === 10, detail: `${s.videos_present || 0} / 10${s.total_video_bytes ? ` · ${(s.total_video_bytes / (1024 * 1024)).toFixed(1)} MB` : ""}` },
    { label: "Quizzes pack", ok: s.quizzes_pack_present, detail: s.quizzes_pack_present ? "loaded" : "missing" },
    { label: "Final exam pack", ok: s.exam_pack_present, detail: s.exam_pack_present ? "loaded" : "missing" },
  ];
  return (
    <div className={`rounded-lg border p-3 ${health.ready ? "border-emerald-500/30 bg-emerald-500/[0.05]" : "border-amber-500/40 bg-amber-500/[0.06]"}`} data-testid="seed-health-strip">
      <div className="flex items-center gap-2 mb-2">
        {health.ready ? (
          <><CheckCircle2 size={14} className="text-emerald-400" /><p className="text-emerald-300 text-xs font-bold">Storage ready — safe to seed</p></>
        ) : (
          <><X size={14} className="text-amber-400" /><p className="text-amber-300 text-xs font-bold">Storage missing assets — seeding blocked</p></>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-1.5 text-[11px]">
            {r.ok ? <CheckCircle2 size={11} className="text-emerald-400 shrink-0" /> : <X size={11} className="text-amber-400 shrink-0" />}
            <span className="text-white font-semibold truncate">{r.label}</span>
            <span className="text-zinc-500 truncate">{r.detail}</span>
          </div>
        ))}
      </div>
    </div>
  );
}



// Small button that migrates all existing certs' verification_url to the
// canonical public domain (shansoai.com/verify-certificate/<id>).
function MigrateCertUrlsButton() {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const run = async () => {
    if (!window.confirm("Update verification URLs of ALL existing certificates to use shansoai.com? This is safe and idempotent.")) return;
    setBusy(true);
    setDone(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/shanso-learn/migrate-cert-verification-urls`,
        {},
        { headers: adminHeaders() }
      );
      setDone(data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Migration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="shrink-0 text-right">
      <button
        onClick={run}
        disabled={busy}
        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-zinc-800 hover:bg-zinc-700 text-zinc-200 disabled:opacity-50 inline-flex items-center gap-1.5"
        title="Rewrite stored verification_url on every cert to use shansoai.com (idempotent)"
        data-testid="migrate-cert-urls-btn"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
        Fix verification URLs
      </button>
      {done && (
        <p className="text-emerald-300 text-[10px] mt-1.5">
          ✓ Updated {done.updated} · skipped {done.already_correct}
        </p>
      )}
    </div>
  );
}

// ── LIVE COHORT CERTS — bulk-issue branded certs for live/cohort programs ──
function LiveCohortCertsTab() {
  const [form, setForm] = useState({
    course_title: "Stop Watching AI, Start Using It",
    course_slug: "stop-watching-ai",
    role: "AI Practitioner",
    body_description:
      "has successfully completed Stop Watching AI, Start Using It and gained practical skills in Prompt Engineering (Advanced Techniques), ChatGPT for Business & Entrepreneurship, API Integrations, Workflow Automation, RAG & Agentic AI, and MCP & Guardrails.",
    issued_on: "19 Apr 2026",
    issued_at_iso: "2026-04-19T10:00:00Z",
    names_csv: "Nivedha R\nSumalini V\nH R Aghil Samaran",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const issue = async () => {
    setBusy(true);
    setErr("");
    setResult(null);
    try {
      const recipients = form.names_csv
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean)
        .map((line) => {
          const parts = line.split(/[|,]/).map((p) => p.trim());
          return { name: parts[0], email: parts[1] || null };
        });
      const { data } = await axios.post(
        `${API}/admin/shanso-learn/issue-live-cohort-certs`,
        {
          course_title: form.course_title,
          course_slug: form.course_slug,
          role: form.role,
          body_description: form.body_description,
          issued_on: form.issued_on,
          issued_at_iso: form.issued_at_iso || null,
          recipients,
        },
        { headers: adminHeaders() }
      );
      setResult(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to issue certificates");
    } finally {
      setBusy(false);
    }
  };

  const dlPdf = async (cert) => {
    try {
      const res = await axios.get(
        `${API}/admin/shanso-learn/certificates/${cert.certificate_id}/pdf`,
        { headers: adminHeaders(), responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = cert.name.replace(/[^A-Za-z0-9]+/g, "_");
      a.download = `ShansoAI_Certificate_${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      alert("Download failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="live-certs-tab">
      <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/[0.04] p-4 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <p className="text-cyan-200 text-sm font-semibold">Bulk-issue certificates for a live or cohort program</p>
          <p className="text-zinc-400 text-xs mt-1">
            Use this for in-person workshops, live webinars, or cohort programs that don't go through the self-paced video flow.
            Each recipient gets a unique certificate ID + public verification URL on shansoai.com.
          </p>
        </div>
        <MigrateCertUrlsButton />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 space-y-3">
          <p className="text-white text-sm font-bold">Program details</p>
          <div>
            <label className="text-[11px] text-zinc-400 font-semibold">Course title</label>
            <input value={form.course_title} onChange={set("course_title")} className="w-full mt-1 bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-white" data-testid="lc-course-title" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-zinc-400 font-semibold">Slug (URL-safe)</label>
              <input value={form.course_slug} onChange={set("course_slug")} className="w-full mt-1 bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-white" data-testid="lc-slug" />
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 font-semibold">Role (top-right badge)</label>
              <input value={form.role} onChange={set("role")} className="w-full mt-1 bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-white" data-testid="lc-role" />
            </div>
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 font-semibold">Body description (starts with "has successfully completed...")</label>
            <textarea value={form.body_description} onChange={set("body_description")} rows={4} className="w-full mt-1 bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-white" data-testid="lc-body" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-zinc-400 font-semibold">Issued on (display)</label>
              <input value={form.issued_on} onChange={set("issued_on")} placeholder="19 Apr 2026" className="w-full mt-1 bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-white" data-testid="lc-issued-on" />
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 font-semibold">Issued-at (ISO, optional)</label>
              <input value={form.issued_at_iso} onChange={set("issued_at_iso")} placeholder="2026-04-19T10:00:00Z" className="w-full mt-1 bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-white" data-testid="lc-issued-at" />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 space-y-3">
          <p className="text-white text-sm font-bold">Recipients</p>
          <p className="text-[11px] text-zinc-400">
            One per line. Format: <code className="text-cyan-300">Full Name</code> or
            <code className="text-cyan-300"> Full Name, email@x.com</code>. Email is optional.
          </p>
          <textarea
            value={form.names_csv}
            onChange={set("names_csv")}
            rows={12}
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm font-mono text-white"
            placeholder="Nivedha R&#10;Sumalini V, sumalini@example.com&#10;H R Aghil Samaran"
            data-testid="lc-recipients"
          />
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={issue}
          disabled={busy}
          className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-400 to-cyan-600 text-black font-bold text-sm inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="lc-issue-btn"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {busy ? "Issuing..." : "Issue Certificates"}
        </button>
        {err && <span className="text-rose-300 text-sm">{err}</span>}
      </div>

      {result && (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/[0.06] p-4" data-testid="lc-result">
          <p className="text-emerald-200 text-sm font-bold inline-flex items-center gap-1.5">
            <CheckCircle2 size={14} /> Issued {result.issued_count} certificate{result.issued_count !== 1 ? "s" : ""}
          </p>
          <div className="mt-3 space-y-2">
            {result.certificates.map((c) => (
              <div key={c.certificate_id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-zinc-950/60 border border-zinc-800">
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{c.name}</p>
                  <p className="text-[11px] text-zinc-500 font-mono truncate">{c.certificate_id}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => dlPdf(c)}
                    className="px-3 py-1.5 rounded text-xs font-bold inline-flex items-center gap-1 bg-cyan-500/15 text-cyan-200 border border-cyan-500/40 hover:bg-cyan-500/25"
                    data-testid={`lc-download-${c.certificate_id}`}
                  >
                    <Download size={12} /> PDF
                  </button>
                  <a
                    href={c.verification_url}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-1.5 rounded text-xs font-bold bg-zinc-800 text-zinc-200 hover:bg-zinc-700"
                  >
                    Verify ↗
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
